import { detectLanguage } from "./language";
import type {
  ClassifiedOrderSetFile,
  OrderSetFileInput,
  OrderSetFormat,
  OrderSetRole,
} from "./types";

const FORMAT_BY_EXTENSION = new Map<string, OrderSetFormat>([
  ["md", "markdown"],
  ["markdown", "markdown"],
  ["docx", "docx"],
  ["jpg", "image"],
  ["jpeg", "image"],
  ["png", "image"],
  ["xlsx", "workbook"],
  ["zip", "archive"],
]);

/** Doc 03 section 4.5 gives `Wng O` as the written form of a warning order. */
const WARNING_ORDER_TOKENS = new Set(["wng", "wngo", "warno", "warning"]);
/** Tokens that can only mean an orders card, never a warning order. */
const ORDERS_CARD_TOKENS = new Set(["ogp", "gp", "card"]);
/** Tokens that mean orders, but that "warning order" also contains. */
const GENERIC_ORDERS_TOKENS = new Set(["orders", "order", "ord"]);
const TRACE_TOKENS = new Set(["trace", "overlay", "talc"]);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return "";
  return name.slice(dot + 1).toLowerCase();
}

function stemOf(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return name;
  return name.slice(0, dot);
}

function tokensOf(stem: string): readonly string[] {
  return stem
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token !== "");
}

function formatOf(extension: string): OrderSetFormat {
  return FORMAT_BY_EXTENSION.get(extension) ?? "unsupported";
}

/**
 * Set identity is the trace or set number the files share. Doc 01 and doc 03
 * both number order sets by trace, so `Trace 4` and `Set 04` reach the same
 * identity and the association never depends on selection order.
 */
function setIdOf(tokens: readonly string[]): string | null {
  const match = /\b(?:trace|set) (\d{1,3})\b/.exec(tokens.join(" "));
  const value = match?.[1];
  if (value === undefined) return null;
  return `trace-${value.padStart(2, "0")}`;
}

/** The same blank token the normalizer reads. Counted, never interpreted. */
const BLANK_TOKEN = /_9C|_{2,}/g;

function blanksIn(text: string | null): number {
  if (text === null) return 0;
  return (text.match(BLANK_TOKEN) ?? []).length;
}

function roleOf(
  tokens: readonly string[],
  format: OrderSetFormat,
): OrderSetRole {
  const trace = tokens.some((token) => TRACE_TOKENS.has(token));
  if (format === "image") return trace ? "trace-image" : "unknown";
  if (format === "workbook" || format === "archive") return "supporting";
  if (format === "unsupported") return "unknown";

  const warningOrder = tokens.some((token) => WARNING_ORDER_TOKENS.has(token));
  const ordersCard = tokens.some((token) => ORDERS_CARD_TOKENS.has(token));
  // "Warning order" contains "order", so a warning-order token outranks the
  // generic orders tokens but never an orders-card-only token.
  if (warningOrder && ordersCard) return "unknown";
  if (warningOrder) return "warning-order";
  if (ordersCard || tokens.some((token) => GENERIC_ORDERS_TOKENS.has(token)))
    return "orders-card";
  // A trace token in a document name is the set label, not a role, so it is
  // never evidence that the document itself is supporting material.
  return "unknown";
}

/**
 * Classifies one supplied file. The name gives the format, the role and the
 * set identity; the **content** gives the language layer, per doc 04 section
 * 5.5 and the ruling of 13 September 2026.
 *
 * Filename tokens no longer decide language at all. A name carries `_FR`
 * only by convention, and the convention held only while one set existed.
 *
 * No path is retained, and a file with no readable text keeps `unmarked`
 * rather than being guessed at.
 */
export function classifyOrderSetFile(
  input: OrderSetFileInput | string,
): ClassifiedOrderSetFile {
  const { name, text } =
    typeof input === "string" ? { name: input, text: null } : input;
  const tokens = tokensOf(stemOf(name));
  const format = formatOf(extensionOf(name));
  const language = detectLanguage(text);

  return {
    blankCount: blanksIn(text),
    format,
    frenchBody: language.frenchBody,
    languageLayer: language.layer,
    name,
    role: roleOf(tokens, format),
    setId: setIdOf(tokens),
  };
}
